import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import { log } from '@/src/lib/logger';

export function useVenueContext(venueId: string | null) {
  const [context, setContext] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!venueId) {
      setContext('');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error: err } = await supabase
        .from('venues')
        .select('custom_context')
        .eq('id', venueId)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        setError(err.message);
      } else {
        setContext((data?.custom_context as string | null) ?? '');
        setError(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [venueId]);

  const save = useCallback(
    async (next: string) => {
      if (!venueId) return { error: 'no venue' };
      setSaving(true);
      const { error: err } = await supabase
        .from('venues')
        .update({ custom_context: next.trim() || null })
        .eq('id', venueId);
      setSaving(false);
      if (err) {
        log.warn('useVenueContext: save failed', { code: err.code });
        setError(err.message);
        return { error: err.message };
      }
      setContext(next);
      setError(null);
      return { error: null };
    },
    [venueId],
  );

  return { context, loading, saving, error, save, setContextLocal: setContext };
}
